import type { AnalyticsEvents } from '@sunflower-games/analytics-events';

import { analytics } from '../../services/analytics';

import type { JackpotCoinType } from './types';

/**
 * The one Crown Jackpot event this app owns: `lobby_widget`.
 *
 * The other four names in the approved list — `popup`, `in_slot_banner`, `spin_wheel`, `win_popup` — are
 * emitted by game-ui, which renders the popup, the in-slot banner and the win ceremony and has its own
 * analytics transport. Nothing is forwarded across the bridge; see
 * `game-ui/src/modules/jackpot/jackpotAnalytics.ts`.
 *
 * ## The taxonomy
 * `event_parent: features` + `event_category: crown_jackpot`. `event_name` is the SURFACE
 * (`lobby_widget`), what the player did rides `event_type`, and the context rides `event_value`. There is
 * no `crown_jackpot_` prefix, no per-state event name and no `button_text`.
 *
 * ## Two events per moment, on purpose
 * The list pairs a state-flavoured event with a pool-flavoured one:
 * - shown  → `opt_in_state` (`opted_in`/`not_opted_in`) **and** `widget_shown` (the pool total)
 * - opened → `opt_in`/`view`/`open_popup` (the state) **and** `open` (the pool total)
 *
 * So a report can read conversion by join state or by pool size without joining two event names.
 *
 * ## `event_env`
 * `cc` / `sc`, from the jackpot coin. Per-event because the list asks for it. It does NOT replace the
 * app-wide `lobby_type` common prop, which stays `CC`/`SC` and is set by `CoinTypeStore` — that one is
 * shared with every other feature and is not this feature's to re-spell.
 *
 * ## Why the casts
 * `lobby_widget` is not a key of `AnalyticsEvents` and `crown_jackpot` is not one of its categories: the
 * whole category belongs in `@sunflower-games/analytics-events` and needs a release. Same pattern as
 * `sendMiniGameEvent` — the name and the payload are asserted at this ONE boundary rather than at each
 * call site, so adopting the typed contract is a single edit here.
 */
type JackpotWidgetEventType = 'opt_in_state' | 'widget_shown' | 'opt_in' | 'view' | 'open_popup' | 'open';

/** `CC` stays `cc` — this taxonomy does not use `gc`. */
export const toEventEnv = (coinType: JackpotCoinType): 'cc' | 'sc' => (coinType === 'SC' ? 'sc' : 'cc');

export const optInStateValue = (optedIn: boolean): 'opted_in' | 'not_opted_in' =>
  optedIn ? 'opted_in' : 'not_opted_in';

interface JackpotWidgetEvent {
  event_action: 'impression' | 'click';
  event_type: JackpotWidgetEventType;
  event_value?: string;
  /** `null` before the group has loaded, in which case `event_env` is left off rather than guessed. */
  coinType: JackpotCoinType | null;
}

function send({ coinType, ...props }: JackpotWidgetEvent): void {
  void analytics.sendEvent(
    'lobby_widget' as keyof AnalyticsEvents,
    {
      event_parent: 'features',
      event_category: 'crown_jackpot',
      ...(coinType ? { event_env: toEventEnv(coinType) } : {}),
      page_url: window.location.href,
      ...props,
    } as any,
  );
}

/** The lobby widget was drawn: one event for the join state, one for the pool total. */
export function trackJackpotWidgetShown(
  coinType: JackpotCoinType | null,
  optedIn: boolean,
  poolAmount: number | null,
): void {
  send({ coinType, event_action: 'impression', event_type: 'opt_in_state', event_value: optInStateValue(optedIn) });
  send({
    coinType,
    event_action: 'impression',
    event_type: 'widget_shown',
    // Left off rather than sent as `0` when the pool has not loaded: a zero jackpot is a real value.
    ...(poolAmount === null ? {} : { event_value: String(poolAmount) }),
  });
}

/**
 * The lobby widget's CTA opted the player in, in place.
 *
 * The `opt_in` click only — deliberately NOT the `open` event its sibling sends, because no popup is
 * opening. `event_value` is the state the player was in when they tapped, matching how every other
 * jackpot click reports it.
 */
export function trackJackpotWidgetOptIn(coinType: JackpotCoinType | null): void {
  send({ coinType, event_action: 'click', event_type: 'opt_in', event_value: optInStateValue(false) });
}

/**
 * The lobby widget was tapped, and the popup is opening.
 *
 * `source` splits the CTA from the rest of the card, which the list distinguishes: the button reports
 * `opt_in` or `view` after its own label, the card body reports `open_popup`. Both also report `open`
 * with the pool.
 */
export function trackJackpotWidgetOpen(
  coinType: JackpotCoinType | null,
  optedIn: boolean,
  poolAmount: number | null,
  source: 'cta' | 'card',
): void {
  const stateEventType: JackpotWidgetEventType = source === 'card' ? 'open_popup' : optedIn ? 'view' : 'opt_in';

  send({ coinType, event_action: 'click', event_type: stateEventType, event_value: optInStateValue(optedIn) });
  send({
    coinType,
    event_action: 'click',
    event_type: 'open',
    ...(poolAmount === null ? {} : { event_value: String(poolAmount) }),
  });
}
